import React from "react";
import { X, Loader2, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export type ExtractionStep = "uploading" | "queued" | "extracting" | "saving" | "completed" | "error";

interface ExtractionSidebarProps {
  isOpen: boolean;
  fileName: string | null;
  currentStep: ExtractionStep;
  /** Hebrew line describing what the backend is doing right now. */
  activity?: string;
  percent?: number;
  pagesProcessed?: number;
  pagesTotal?: number;
  errorMessage?: string;
  onClose?: () => void;
  onRetry?: () => void;
  onCancel?: () => void;
}

const ExtractionSidebar: React.FC<ExtractionSidebarProps> = ({
  isOpen,
  fileName,
  currentStep,
  activity,
  percent = 0,
  pagesProcessed,
  pagesTotal,
  errorMessage,
  onClose,
  onRetry,
  onCancel,
}) => {
  const steps = [
    { key: "uploading", label: "מעלה קובץ" },
    { key: "queued", label: "ממתין לעיבוד" },
    { key: "extracting", label: "מחלץ טקסט" },
    { key: "saving", label: "שומר תוצאות" },
  ];

  const getStepStatus = (stepKey: string) => {
    const stepOrder = ["uploading", "queued", "extracting", "saving"];
    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(stepKey);

    if (currentStep === "error") {
      return stepIndex < currentIndex ? "completed" : stepIndex === currentIndex ? "error" : "pending";
    }

    if (currentStep === "completed") {
      return "completed";
    }

    if (stepIndex < currentIndex) {
      return "completed";
    } else if (stepIndex === currentIndex) {
      return "active";
    } else {
      return "pending";
    }
  };

  const canDismiss = currentStep === "error" || currentStep === "completed";

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && canDismiss) {
          onClose?.();
        }
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full max-w-full flex-col gap-0 p-0 sm:max-w-[600px] [&>button]:hidden"
      >
        <SheetHeader className="gap-0 space-y-0 bg-chen-900 p-0 text-white">
          <div className="flex items-center justify-between p-6 pb-2">
            <SheetTitle className="text-lg font-semibold text-white">
              {currentStep === "error" ? "שגיאה בחילוץ המסמך" : "מעבד מסמך..."}
            </SheetTitle>
            <div className="flex items-center gap-1">
              {onCancel && currentStep !== "completed" && currentStep !== "error" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  className="text-white hover:bg-white/20"
                >
                  ביטול
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={!canDismiss}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <p className="truncate px-6 pb-6 text-sm text-white/80 text-start">{fileName}</p>
        </SheetHeader>

        <div className="border-b border-border p-6">
          <div className="space-y-3">
            {steps.map((step) => {
              const status = getStepStatus(step.key);
              return (
                <div key={step.key} className="flex items-center gap-3">
                  {status === "completed" && (
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  )}
                  {status === "active" && (
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
                  )}
                  {status === "error" && (
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  )}
                  {status === "pending" && (
                    <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                  )}
                  <span
                    className={`text-sm ${
                      status === "completed"
                        ? "text-green-600"
                        : status === "active"
                        ? "text-blue-600 font-medium"
                        : status === "error"
                        ? "text-red-600"
                        : "text-muted-foreground"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {currentStep === "error" && (
          <div className="border-b border-border bg-red-50 p-6 dark:bg-red-950/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <div className="flex-1">
                <p className="mb-1 text-sm font-medium text-red-900 dark:text-red-100">הבעיה:</p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {errorMessage || "שגיאה לא ידועה"}
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  נסה שוב
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                סגור
              </Button>
            </div>
          </div>
        )}

        {currentStep !== "error" && (
          <div className="flex-1 overflow-hidden p-6">
            <div className="flex h-full flex-col gap-4">
              <h3 className="text-sm font-medium text-foreground">התקדמות</h3>

              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-chen-900 transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
                />
              </div>

              <p className="text-sm text-muted-foreground">
                {activity || "ממתין לתוצאות..."}
              </p>

              {typeof pagesTotal === "number" && pagesTotal > 0 && (
                <p className="text-sm text-foreground">
                  {pagesProcessed ?? 0} / {pagesTotal} עמודים
                </p>
              )}

              <p className="mt-auto text-xs text-muted-foreground">
                החילוץ מתבצע בשרת. סגירת החלון תפסיק את המעקב אחרי ההתקדמות.
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ExtractionSidebar;
